import posthog from 'posthog-js';

// --- PostHog event throttling -------------------------------------------------
// A handful of error/warning call sites live inside 15s/30s polling & retry loops.
// On a misbehaving device they emit the SAME event thousands of times a day, which
// is what pushed the project past PostHog's 1M-event free tier (a single stuck photo
// produced ~200k "Order not found" events in a month). We de-duplicate per
// (level + normalized message + screen): the first occurrence is sent immediately,
// then at most one every THROTTLE_WINDOW_MS, carrying how many were suppressed in
// between (`suppressedSinceLast`) so the real magnitude is never lost.
const THROTTLE_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const eventState = new Map(); // key -> { last: epochMs, suppressed: number }

// Collapse high-cardinality tokens (uuids, long numbers, photo paths) so the many
// loop variants of the "same" event share a single throttle bucket and group
// cleanly in PostHog instead of exploding into thousands of unique event names.
function normalizeEventName(name) {
  return String(name)
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<id>')
    .replace(/[0-9]{3,}/g, '<n>')
    .trim();
}

// Throttle gate. Returns the number of suppressed events since the last send
// (0 on a send that wasn't throttled), or null when this event must be suppressed.
function throttleDecision(key, now) {
  const rec = eventState.get(key);
  if (!rec) {
    eventState.set(key, { last: now, suppressed: 0 });
    return 0;
  }
  if (now - rec.last >= THROTTLE_WINDOW_MS) {
    const suppressed = rec.suppressed;
    rec.last = now;
    rec.suppressed = 0;
    return suppressed;
  }
  rec.suppressed += 1;
  return null;
}

class Logger {
  constructor(defaultProperties = {}) {
    this.defaultProperties = defaultProperties;
    this.dev = process.env.NODE_ENV === 'development';
  }

  log(level, event, properties = {}) {
    // Some call sites pass the Error itself as the second argument instead of
    // { error }. Normalize here so every path below sees the same shape and the
    // Error's stack still drives the Error Tracking grouping.
    if (properties instanceof Error) {
      properties = { error: properties };
    }

    const displayEvent = `[${level.toUpperCase()}]`;

    // Dev console output for every level (unchanged behaviour).
    if (this.dev) {
      if (level === 'error') {
        console.error(displayEvent, event, properties);
      } else if (level === 'warning') {
        console.warn(displayEvent, event, properties);
      } else if (level === 'success') {
        console.info(displayEvent, event, properties);
      } else if (level === 'debug') {
        console.debug(displayEvent, event, properties);
      } else {
        console.log(displayEvent, event, properties);
      }
    }

    // Only errors and warnings are reported to PostHog (info/success/debug are console-only).
    if (level !== 'error' && level !== 'warning') {
      return;
    }

    // De-duplicate loop spam before sending anything to PostHog.
    const screen = this.defaultProperties.screen || 'unknown';
    const baseName = typeof event === 'string' ? event : `${level} event`;
    const normalized = normalizeEventName(baseName);
    const suppressed = throttleDecision(`${level}|${screen}|${normalized}`, Date.now());
    if (suppressed === null) {
      return;
    }

    const eventProps = { ...this.defaultProperties, ...properties, level, timestamp: new Date().toISOString() };
    if (suppressed > 0) {
      eventProps.suppressedSinceLast = suppressed;
    }

    // Extract error details if an Error object was passed.
    let errorObj = null;
    if (properties.error instanceof Error) {
      errorObj = properties.error;
      eventProps.errorMessage = properties.error.message;
      eventProps.errorName = properties.error.name;
      eventProps.errorStack = properties.error.stack;
      delete eventProps.error;
    }

    if (level === 'error') {
      // Route errors through PostHog Error Tracking (separate, cheaper bucket, grouped
      // by fingerprint) instead of generic analytics events. Prefer the real Error so
      // its stack drives the grouping; otherwise synthesize one from the normalized name.
      eventProps.eventName = baseName;
      posthog.captureException(errorObj || new Error(normalized), eventProps);
    } else {
      // Warnings stay as (throttled) analytics events to preserve existing dashboards.
      posthog.capture(baseName, eventProps);
    }
  }

  console(event) {
    if (this.dev) {
      if (typeof event === 'object' && event !== null) {
        console.log('[DEV CONSOLE]', JSON.parse(JSON.stringify(event)));
      } else {
        console.log('[DEV CONSOLE]', event);
      }
    }
  }

  info(event, properties = {}) {
    this.log('info', event, properties);
  }

  warning(event, properties = {}) {
    this.log('warning', event, properties);
  }

  error(event, properties = {}) {
    this.log('error', event, properties);
  }

  success(event, properties = {}) {
    this.log('success', event, properties);
  }

  debug(event, properties = {}) {
    this.log('debug', event, properties);
  }

  identify(userId) {
    posthog.identify(userId);
    if (this.dev) {
      console.log(`[IDENTIFICATION] Identified user: ${userId}`);
    }
  }

  setUserProperties(properties) {
    posthog.people.set({
      ...this.defaultProperties,
      ...properties
    });
  }
}

export default Logger;
